import { Button, Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { renderPackage } from '../../../../../shared/helpers/renderLocator';
import { ErrorAlert } from '../../../generic';
import Loader from '../../../generic/Loader';

import RelationshipList from './RelationshipList';
import useDependencyPaths from './useDependencyPaths';

type Props = {
  parentLocator: string;
  revision?: {
    locator: string;
    project: { title: string };
  };
  show: boolean;
  onHide(): void;
};

const NoPathDataDisplay = () => (
  <div className="text-center">
    <h4>No Path Data</h4>
    <p>
      FOSSA failed to find path data for this dependency. This dependency may have been reported in a method
      that does not support paths, or it could disappear in the next build.
    </p>
  </div>
);

const ModalViewRelationshipsBody: VoidFunctionComponent<Pick<Props, 'parentLocator' | 'revision'>> = ({
  parentLocator,
  revision,
}) => {
  const { isLoading, paths, errors, dependencyLocks } = useDependencyPaths({
    root: parentLocator,
    target: revision?.locator?.toString() || '',
  });

  let projectTitle: string | null = revision?.project.title || null;
  if (!projectTitle) {
    projectTitle = revision?.locator ? renderPackage(revision.locator) : null;
  }

  if (errors) {
    return <ErrorAlert status={{ errors }} />;
  }
  if (isLoading || !revision) {
    return <Loader />;
  }
  if (paths.length === 0) {
    return <NoPathDataDisplay />;
  }
  return (
    <RelationshipList
      paths={paths}
      projectTitle={projectTitle}
      dependencyLocks={dependencyLocks}
    />
  );
};

const ModalViewRelationships: VoidFunctionComponent<Props> = ({
  show,
  onHide,
  parentLocator,
  revision,
}) => {
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Relationships</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalViewRelationshipsBody
          parentLocator={parentLocator}
          revision={revision}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalViewRelationships;
